import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import RatesAuditApi from './Audit/Apis/RatesAuditApi';
import ActionHelper from '../../Common/Actions/ActionHelper';
import NotificationActions from '../../Common/Actions/NotificationActions';
import { saveResponseAsAsync } from '../../Common/Utilities/FileUtilities';
import { RatesRootState } from '../Redux/RatesStore';

type AuditRecord = {
    rateCardActionAuditId: number;
    ratesOwner: {
        name: string;
        businessId: number;
    };
    actionCompletedAt: string;
    loggingActivityId: string;
    performedByUser: {
        fullName: string;
        userId: number;
    };
    rateCardAction: string;
};

type AuditRecords = {
    rows: AuditRecord[];
};

export type RateAuditPageViewState = {
    auditRecords: AuditRecords | undefined;
};

const initialStateValue: RateAuditPageViewState = {
    auditRecords: undefined
};

const baseReducerName = "RateAuditPageViewState";

const rateAuditPageViewStateSlice = createSlice({
    name: baseReducerName,
    initialState: initialStateValue,
    reducers: {
        clearAuditRecords(state) {
            state.auditRecords = undefined;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadAuditRecord.fulfilled, (state, action) => {
                state.auditRecords = action.payload;
            });
    }
});

type RaiseAuditRecordNotificationsArgs = {
    rows: unknown[];
    apiCutOffExceeded: boolean;
};

function raiseAuditRecordNotifications(auditRecords: RaiseAuditRecordNotificationsArgs) {
    if (auditRecords.rows.length === 0) {
        NotificationActions.notificationReceived({
            icon: 'warning',
            severity: 'information',
            title: 'No rate card audit records could be found.',
            messages:['No rate card audit records could be found matching your date range. Please modify your date range and try again.']
        });
    }
    else if (auditRecords.apiCutOffExceeded) {
        const cutoffResultCount = 100;
        NotificationActions.notificationReceived({
            icon: 'warning',
            severity: 'information',
            title: `More rate card audit records were found than could be displayed or downloaded.`,
            messages: ['Your search returned more than ' + cutoffResultCount + ' rate card audit records. Only the first ' + cutoffResultCount + ' will be displayed or downloaded. Please modify your date range to filter this down.']
        });
    }
}

export type LoadAuditRecordArgs = {
    fromInclusive: Date;
    toExclusive: Date;
};

const loadAuditRecord = createAsyncThunk(
    `${baseReducerName}/loadAuditRecord`,
    async (loadAuditRecordArgs: LoadAuditRecordArgs) => {
        const response = await ActionHelper.executeRequest(() => RatesAuditApi.loadAuditRecords(loadAuditRecordArgs.fromInclusive, loadAuditRecordArgs.toExclusive));
        raiseAuditRecordNotifications(response);
        return response;
    }
);

type DownloadFileArgs = {
    rateCardActionAuditId: number;
    action: string;
    fileName: string;
};
const downloadFile = createAsyncThunk(
    `${baseReducerName}/downloadFile`,
    async (downloadFileArgs: DownloadFileArgs) => {
        const response = await ActionHelper.executeRequest(
            () => RatesAuditApi.downloadFile(downloadFileArgs.rateCardActionAuditId, downloadFileArgs.action)
        );
        await saveResponseAsAsync(response, downloadFileArgs.fileName);
    }
);

type DownloadAuditFileArgs = {
    from: Date;
    to: Date;
    fileName: string;
};
const downloadAuditFile = createAsyncThunk(
    `${ baseReducerName }/downloadAuditFile`,
    async (downloadAuditFileArgs: DownloadAuditFileArgs) => {
        const auditRecordsResponse = await ActionHelper.executeRequest(() =>
            RatesAuditApi.loadAuditRecords(downloadAuditFileArgs.from, downloadAuditFileArgs.to)
        );
        raiseAuditRecordNotifications(auditRecordsResponse);
        const rateCardActionAuditIds = auditRecordsResponse.rows.map((a: { rateCardActionAuditId: number }) => a.rateCardActionAuditId);
        const response = await ActionHelper.executeRequest(() =>
            RatesAuditApi.downloadAuditReportFile(rateCardActionAuditIds)
        );
        await saveResponseAsAsync(response, downloadAuditFileArgs.fileName);
    }
);

// reducer for the main store.
export default rateAuditPageViewStateSlice.reducer;

// reducers
export const {
    clearAuditRecords
} = rateAuditPageViewStateSlice.actions;

// selectors
export const selectAuditRecords = (state: RatesRootState) => state.rateAuditPageView.auditRecords;

// thunks
export {
    loadAuditRecord,
    downloadFile,
    downloadAuditFile
};
